import React from 'react';
import { TrendChartDataSet, TrendPoint } from './TrendsChart';
import { partitionLines } from '../../util/line';
import { Line as D3Line } from 'd3';
import {
  COMPARISON_STROKE_DASH_ARRAY,
  Line,
  PARTIAL_TIME_PERIOD_COMPARISON_STROKE_DASH_ARRAY,
  PARTIAL_TIME_PERIOD_STROKE_DASH_ARRAY,
} from '../Line';
import { ChartPoint } from '../ChartPoint';
import { TrendDataSet } from '../../util/types';
import { TooltipCoordinateData } from '../..';

const UNFOCUSED_OPACITY = 0.2;

interface TrendChartLineProps<P extends TrendPoint> {
  data: TrendChartDataSet<P>;
  showPartialPeriods: boolean;
  lineFunction: (d: TrendDataSet) => D3Line<P>;
  focused?: boolean;
  dotRadius: number;
  tooltipCoordinateData?: TooltipCoordinateData<P> | null;
  getPointX: (p: P) => number;
  getPointY: (p: P) => number;
}

export function TrendChartLine<P extends TrendPoint>({
  data,
  showPartialPeriods,
  lineFunction,
  focused = true,
  dotRadius,
  tooltipCoordinateData,
  getPointX,
  getPointY,
}: TrendChartLineProps<P>) {
  return (
    // eslint-disable-next-line @fullstory/annotate-react/data-component
    <>
      {showPartialPeriods ? (
        partitionLines(data.points).map(line => (
          <Line<P>
            key={`${data.id}-line-${line.indexes.join(',')}`}
            line={lineFunction(data)}
            data={line.points}
            color={data.fill}
            opacity={!focused ? UNFOCUSED_OPACITY : undefined}
            strokeDasharray={getStrokeDasharray(data, line.isPartialPeriod)}
            {...data.lineProps}
          />
        ))
      ) : (
        <Line<P>
          key={`${data.id}-line`}
          line={lineFunction(data)}
          data={data.points}
          color={data.fill}
          opacity={!focused ? UNFOCUSED_OPACITY : undefined}
          strokeDasharray={data.isComparison ? COMPARISON_STROKE_DASH_ARRAY : undefined}
          {...data.lineProps}
        />
      )}
      {tooltipCoordinateData &&
        data.points.map(p => {
          const x = getPointX(p);
          if (x === tooltipCoordinateData.x) {
            const y = getPointY(p);
            return (
              <ChartPoint
                key={`${x}-${y}-${data.fill}`}
                r={dotRadius}
                x={x}
                y={y}
                color={data.fill}
                hollow={showPartialPeriods ? p.isPartialPeriod : false}
                highlight={tooltipCoordinateData.x === x && tooltipCoordinateData.y === y}
              />
            );
          }
          return null;
        })}
    </>
  );
}

function getStrokeDasharray<P extends TrendPoint>(
  d: TrendChartDataSet<P>,
  isPartialPeriodLine: boolean,
) {
  if (isPartialPeriodLine) {
    return d.isComparison
      ? PARTIAL_TIME_PERIOD_COMPARISON_STROKE_DASH_ARRAY
      : PARTIAL_TIME_PERIOD_STROKE_DASH_ARRAY;
  }

  return d.isComparison ? COMPARISON_STROKE_DASH_ARRAY : undefined;
}
